/**
 * Unified Product Selector
 * All-in-one: collection grid + size picker + buy button
 * Handles AJAX product switching, size persistence, and form interception.
 */
(function () {
  'use strict';

  // Guard against duplicate script execution when UPS snippet is rendered multiple times.
  if (window.__upsBootstrapped) return;
  window.__upsBootstrapped = true;

  // Market/locale root (e.g. "/en-ca/" on the Canadian store, "/" on the US store).
  // Every store URL this script builds MUST be prefixed with this so an AJAX color
  // switch never drops the market and bounces the shopper to another storefront.
  // Matches the convention used throughout assets/theme.js (Shopify.routes.root).
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Shared section HTML cache across all UPS instances (FIFO, max 30 entries)
  const CACHE_MAX = 30;
  const sectionCache = window.__upsSectionCache || new Map();
  window.__upsSectionCache = sectionCache;
  function cacheSet(key, value) {
    sectionCache.delete(key); // Move to end if exists
    sectionCache.set(key, value);
    if (sectionCache.size > CACHE_MAX) {
      sectionCache.delete(sectionCache.keys().next().value);
    }
  }
  const canHoverPrefetch = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const refreshKlaviyoBis = () => {
    if (!document.querySelector('[data-klaviyo-bis-placeholder], .klaviyo-bis-trigger')) return;
    try {
      if (typeof window.refreshKlaviyoBis === 'function') {
        window.refreshKlaviyoBis();
      }
    } catch (_) {}
  };

  // Registry of UPS instances keyed by formId, so a size selection in one
  // instance (e.g. mobile) can mark its mirror (desktop) as selected — the
  // sticky bar always submits the desktop form, whose own interceptor would
  // otherwise block the first-visit add.
  const upsInstances = window.__upsInstances || new Map();
  window.__upsInstances = upsInstances;

  class UnifiedProductSelector {
    constructor(el) {
      this.el = el;
      this.configEl = el.querySelector('[data-ups-config]');
      if (!this.configEl) return;

      try {
        this.config = JSON.parse(this.configEl.textContent);
      } catch (e) {
        console.error('[UPS] Config parse error:', e);
        return;
      }

      // CA market: variant ids out of stock at the Canadian location (in stock globally via US).
      // These must read as "Sold out" and be non-addable even though variant.available is true.
      this.caOos = new Set(this.config.caOosVariantIds || []);
      // Publish for the shared add-to-cart guard (theme.js ProductForm) and the sticky bar.
      // Replace (not union) so switching products doesn't leak stale ids into the block-list.
      window.KANE_CA_OOS = new Set(this.config.caOosVariantIds || []);

      this.sectionId = el.dataset.sectionId;
      this.formId = el.dataset.formId;
      if (this.formId) upsInstances.set(this.formId, this);
      this.currentHandle = el.dataset.currentHandle;
      this.sizeExplicitlySelected = false;
      this.activeTab = null;
      this.switching = false;
      this.scrollState = new Map();
      this.isSubmitting = false;
      this.showingAddedFeedback = false;
      this.addedFeedbackTimer = null;
      this.previousVariant = null;
      this.switchController = null;

      // Store current page template for cross-template redirect detection
      const buyButtons = el.querySelector('buy-buttons');
      this.currentTemplate = buyButtons ? (buyButtons.getAttribute('template') || '') : '';

      this.init();
    }

    init() {
      this.bindCollectionTabs();
      this.bindProductClicks();
      this.bindSizeSelection();
      this.bindFormInterception();
      this.bindFormSubmitLoading();
      this.setInitialButtonState();
      this.initBisTrigger();
      this.restoreState();
      this.syncInitialVariantState();
      refreshKlaviyoBis();

      // Auto-select first available size if not required
      if (!this.config.requireSizeSelection && this.config.hasSizeOption && !this.sizeExplicitlySelected) {
        this.autoSelectFirstSize();
      }

      if (this.config.mobileScrollAlignment && this.isMobile()) {
        this.initMobileScroll();
      }

      this.bindResizeSync();
    }

    /* Sync size selection when viewport crosses the mobile/desktop boundary.
       Both mobile and desktop UPS instances exist in DOM; only one is visible.
       When visibility switches, read shared sessionStorage and apply to this instance. */
    bindResizeSync() {
      if (this._resizeSyncBound) return;
      this._resizeSyncBound = true;

      const mq = window.matchMedia('(max-width: 999px)');
      mq.addEventListener('change', () => {
        // Only act if this instance's container is now visible
        if (this.el.offsetHeight === 0) return;

        let state = null;
        try {
          const raw = sessionStorage.getItem('ups-state');
          if (raw) state = JSON.parse(raw);
        } catch (_) {}

        if (state && state.size && this.config.hasSizeOption) {
          this.restoreSize(state.size);
        }
      });
    }

    /* ═══════════════════════════════════════════════
       Collection Tabs
       ═══════════════════════════════════════════════ */
    bindCollectionTabs() {
      const tabs = this.el.querySelectorAll('.ups__filter-tab');
      if (!tabs.length) return;

      // Set initial active tab
      const activeBtn = this.el.querySelector('.ups__filter-tab.active');
      if (activeBtn) this.activeTab = activeBtn.dataset.collectionHandle;

      // Ensure the tab containing the current product is active
      this.ensureCorrectTab(tabs);

      tabs.forEach((btn, i) => {
        btn.addEventListener('click', () => {
          const handle = btn.dataset.collectionHandle;
          if (handle === this.activeTab) return;

          tabs.forEach(b => {
            b.classList.remove('active');
            b.setAttribute('aria-selected', 'false');
          });
          btn.classList.add('active');
          btn.setAttribute('aria-selected', 'true');

          this.switchTab(handle);
        });

        btn.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft' && i > 0) {
            e.preventDefault();
            tabs[i - 1].focus();
          } else if (e.key === 'ArrowRight' && i < tabs.length - 1) {
            e.preventDefault();
            tabs[i + 1].focus();
          }
        });
      });
    }

    ensureCorrectTab(tabs) {
      // Find which panel contains the currently selected product
      const selectedCard = this.el.querySelector('.ups__product.is-selected');
      if (!selectedCard) return;

      const correctPanel = selectedCard.closest('.ups__grid-panel');
      if (!correctPanel) return;

      const correctHandle = correctPanel.dataset.collectionHandle;
      if (correctHandle && correctHandle !== this.activeTab) {
        // The active tab doesn't match where the selected product is — fix it
        tabs.forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-selected', 'false');
        });
        const correctBtn = this.el.querySelector(`.ups__filter-tab[data-collection-handle="${correctHandle}"]`);
        if (correctBtn) {
          correctBtn.classList.add('active');
          correctBtn.setAttribute('aria-selected', 'true');
        }
        this.switchTab(correctHandle);
      }
    }

    switchTab(handle) {
      const panels = this.el.querySelectorAll('.ups__grid-panel');
      panels.forEach(p => {
        const isActive = p.dataset.collectionHandle === handle;
        p.classList.toggle('active', isActive);
        p.toggleAttribute('hidden', !isActive);
      });
      this.activeTab = handle;
      this.saveState();

      if (this.isMobile() && this.config.mobileScrollAlignment) {
        requestAnimationFrame(() => this.scrollToSelected(handle));
      }

      if (this.config.showSizeSpecificOos && this.sizeExplicitlySelected) {
        this.updateSizeSpecificOos();
      }
    }

    /* ═══════════════════════════════════════════════
       Product Click → AJAX Switch
       ═══════════════════════════════════════════════ */
    bindProductClicks() {
      // Prefetch only on devices where hover is intentional (avoid mobile scroll-trigger churn).
      if (canHoverPrefetch) {
        this.el.addEventListener('mouseenter', (e) => {
          const link = e.target.closest('.ups__product');
          if (!link) return;
          const handle = link.dataset.productHandle;
          const targetTemplate = link.dataset.productTemplate || '';
          // Skip prefetch for cross-template products (they'll do a full page nav)
          if (handle && handle !== this.currentHandle && targetTemplate === this.currentTemplate) {
            this.prefetch(handle);
          }
        }, true);
      }

      this.el.addEventListener('click', (e) => {
        const link = e.target.closest('.ups__product');
        if (!link) return;

        const handle = link.dataset.productHandle;
        if (!handle || handle === this.currentHandle || this.switching) return;

        // Cross-template products → full page navigation (let the <a> href work)
        const targetTemplate = link.dataset.productTemplate || '';
        if (targetTemplate !== this.currentTemplate) return;

        // Same template → AJAX switch
        e.preventDefault();
        this.switchProduct(handle, link);
      });
    }

    prefetch(handle) {
      const url = `${ROOT}products/${handle}?section_id=${this.sectionId}`;
      if (sectionCache.has(url)) return;
      // Store the promise so concurrent requests share one fetch
      const promise = fetch(url, { credentials: 'same-origin' })
        .then(r => r.ok ? r.text() : Promise.reject())
        .catch(() => { sectionCache.delete(url); });
      cacheSet(url, promise);
    }

    async switchProduct(handle, clickedEl) {
      // Cancel any in-flight product switch fetch
      if (this.switchController) this.switchController.abort();
      this.switchController = new AbortController();
      const signal = this.switchController.signal;

      this.switching = true;
      this.saveState();
      const trueMedState = this.captureTrueMedState();

      // Clean up any loading state
      this.isSubmitting = false;
      this.showingAddedFeedback = false;
      if (this.addedFeedbackTimer) {
        clearTimeout(this.addedFeedbackTimer);
        this.addedFeedbackTimer = null;
      }
      this.clearButtonLoading();

      // Visual feedback
      if (clickedEl) clickedEl.classList.add('is-loading');

      try {
        const url = `${ROOT}products/${handle}?section_id=${this.sectionId}`;

        // Use cached/prefetched response, or fetch fresh
        let html;
        if (sectionCache.has(url)) {
          html = await sectionCache.get(url);
        }
        if (!html) {
          const res = await fetch(url, { credentials: 'same-origin', signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          html = await res.text();
        }
        // Cache for revisits
        cacheSet(url, Promise.resolve(html));
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');

        // Preserve scroll position across DOM swap
        const scrollY = window.scrollY;
        const isMobileSwitch = this.isMobile();

        // Find the product-rerender container in the response
        const newRerender = parsedDoc.querySelector('product-rerender') || parsedDoc.querySelector('[id^="product-rerender"]');
        const currentRerender = document.querySelector('product-rerender') || document.querySelector('[id^="product-rerender"]');

        if (newRerender && currentRerender) {
          currentRerender.replaceWith(newRerender);
        } else {
          // Fallback: find our UPS container in the fragment and replace it
          const newUps = parsedDoc.querySelector('[data-ups]');
          if (newUps) {
            this.el.replaceWith(newUps);
          }
        }

        this.restoreTrueMedState(trueMedState);

        // Restore scroll position after DOM swap
        window.scrollTo(0, scrollY);

        // Update URL + title (include variant param so Klaviyo can read product context)
        const newForm = document.getElementById(this.formId);
        const variantInput = newForm ? newForm.querySelector('input[name="id"]') : null;
        const variantId = variantInput ? variantInput.value : '';
        const variantParam = variantId ? `?variant=${variantId}` : '';
        history.pushState({}, '', `${ROOT}products/${handle}${variantParam}`);
        const newUpsEl = document.querySelector('[data-ups]');
        if (newUpsEl && newUpsEl.dataset.productTitle) {
          document.title = newUpsEl.dataset.productTitle;
        }

        // Re-init Shopify payment buttons
        if (window.Shopify && window.Shopify.PaymentButton) {
          try { window.Shopify.PaymentButton.init(); } catch (_) {}
        }

        // UPS replaces the DOM; refresh Klaviyo BIS so new triggers open the modal.
        refreshKlaviyoBis();

        // Re-assert scroll after async post-swap work, but avoid extra jump on mobile Safari.
        if (!isMobileSwitch) {
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollY);
          });
        }

        // Scroll to top of product section to re-anchor attention (disabled)
        // const productSection = document.querySelector('.product') || document.querySelector('[data-ups]');
        // if (productSection) {
        //   productSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // }

        // Fire custom event
        document.dispatchEvent(new CustomEvent('ups:product-switched', {
          detail: { handle, sectionId: this.sectionId, sectionHTML: html }
        }));

        // The new UPS auto-initializes via the init() at bottom of this file
      } catch (err) {
        if (err.name === 'AbortError') return; // Cancelled by a newer switch
        console.error('[UPS] Product switch failed:', err);
        if (clickedEl) clickedEl.classList.remove('is-loading');
      } finally {
        this.switching = false;
      }
    }

    /* ═══════════════════════════════════════════════
       Size Selection
       ═══════════════════════════════════════════════ */
    bindSizeSelection() {
      if (!this.config.hasSizeOption) return;

      const sizesContainer = this.el.querySelector('[data-ups-sizes]');
      if (!sizesContainer) return;

      sizesContainer.addEventListener('change', (e) => {
        if (e.target.type !== 'radio') return;

        this.sizeExplicitlySelected = true;
        const selectedSize = e.target.getAttribute('title') || e.target.value;

        this.updateVariantId(selectedSize);
        this.updateSizeLabel(selectedSize);
        this.updateButtonState();
        this.hideError();
        this.saveState();

        if (this.config.showSizeSpecificOos) {
          this.updateSizeSpecificOos();
        }

        // Dispatch variant:change for gallery updates
        this.dispatchVariantChange();
      });
    }

    updateVariantId(selectedSize) {
      const variants = this.config.variants;
      if (!variants || !variants.length) return;

      const sizeIdx = this.config.sizeOptionIndex || 0;
      const variant = variants.find(v => {
        const opt = v.options ? v.options[sizeIdx] : (v['option' + (sizeIdx + 1)]);
        return opt === selectedSize;
      });

      if (!variant) return;

      const form = document.getElementById(this.formId);
      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variant.id;
        idInput.removeAttribute('disabled');
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Sync variant ID to the other form (mobile ↔ desktop) so the sticky
      // bar always submits the correct variant regardless of viewport.
      const otherFormId = this.formId.includes('-mobile-')
        ? this.formId.replace('-mobile-', '-')
        : this.formId.replace('product-form-', 'product-form-mobile-');
      const otherForm = document.getElementById(otherFormId);
      if (otherForm) {
        const otherInput = otherForm.querySelector('input[name="id"]');
        if (otherInput) {
          otherInput.value = variant.id;
          otherInput.removeAttribute('disabled');
        }
      }

      // Keep the mirror instance's gate in sync so its submit interceptor
      // (bindFormInterception) won't block a sticky-bar add of this variant.
      const otherUps = upsInstances.get(otherFormId);
      if (otherUps && this.sizeExplicitlySelected) otherUps.sizeExplicitlySelected = true;

      this.syncProductAvailabilityClasses(variant);
      return variant;
    }

    updateSizeLabel(size) {
      const label = this.el.querySelector('[data-size-selected-label]');
      if (label) label.textContent = size;
    }

    dispatchVariantChange() {
      const form = document.getElementById(this.formId);
      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;

      const variantId = parseInt(idInput.value, 10);
      const variant = (this.config.variants || []).find(v => v.id === variantId);

      if (variant) {
        form.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            formId: form.id,
            variant,
            previousVariant: this.previousVariant
          }
        }));
        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variant, sectionId: this.sectionId }
        }));
        this.previousVariant = variant;
      }
    }

    syncInitialVariantState() {
      const form = document.getElementById(this.formId);
      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;

      const variantId = parseInt(idInput.value, 10);
      const variant = variantId ? (this.config.variants || []).find(v => v.id === variantId) : null;
      if (!variant) return;

      this.previousVariant = variant;
      this.syncProductAvailabilityClasses(variant);
    }

    // CA market: a variant OOS at the Canadian location counts as unavailable even if globally available.
    isEffectivelyAvailable(variant) {
      return !!(variant && variant.available && !this.caOos.has(variant.id));
    }

    syncProductAvailabilityClasses(variant) {
      if (!variant || !document.body.classList.contains('template-product')) return;

      const productEl = document.getElementsByClassName('product')[0];
      if (!productEl) return;

      if (this.isEffectivelyAvailable(variant)) {
        productEl.classList.add('product-available');
        productEl.classList.remove('product-unavailable');
      } else {
        productEl.classList.add('product-unavailable');
        productEl.classList.remove('product-available');
      }
    }

    captureTrueMedState() {
      const nodes = Array.from(document.querySelectorAll('#truemed-instructions'));
      if (!nodes.length) return null;

      const sourceNode = nodes.find((node) => node.innerHTML && node.innerHTML.trim().length > 0) || nodes[0];
      if (!sourceNode) return null;

      return {
        html: sourceNode.innerHTML,
        publicId: sourceNode.getAttribute('data-public-id') || ''
      };
    }

    restoreTrueMedState(state) {
      if (!state || !state.html) return;

      const nodes = document.querySelectorAll('#truemed-instructions');
      nodes.forEach((node) => {
        if (!node) return;
        if (node.innerHTML && node.innerHTML.trim().length > 0) return;
        if (state.publicId) node.setAttribute('data-public-id', state.publicId);
        node.innerHTML = state.html;
      });
    }

    /* ═══════════════════════════════════════════════
       Form Interception (size enforcer)
       ═══════════════════════════════════════════════ */
    bindFormInterception() {
      if (!this.config.hasSizeOption) return;

      const form = document.getElementById(this.formId);
      if (!form) return;

      form.addEventListener('submit', (e) => {
        if (this.config.requireSizeSelection && !this.sizeExplicitlySelected) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.showSizeError();
          return false;
        }
      }, true); // Capture phase to run before product-form handler
    }

    /* ═══════════════════════════════════════════════
       Form Submit Loading State
       ═══════════════════════════════════════════════ */
    bindFormSubmitLoading() {
      const form = document.getElementById(this.formId);
      if (!form) return;

      // Track submission state
      form.addEventListener('submit', () => {
        if (this.config.requireSizeSelection && !this.sizeExplicitlySelected) {
          return; // Validation will prevent submit
        }
        this.isSubmitting = true;
      });

      // The custom-button component automatically sets aria-busy="true" on submit
      // We just need to clean it up when cart operations complete

      // Listen for cart addition success/failure
      form.addEventListener('variant:add', () => {
        this.isSubmitting = false;
        this.clearButtonLoading();
        this.showAddedFeedback();
      });

      form.addEventListener('cart:error', () => {
        this.isSubmitting = false;
        this.showingAddedFeedback = false;
        this.clearButtonLoading();
        this.updateButtonState();
      });

      // Fallback: reset loading state after timeout
      form.addEventListener('submit', () => {
        setTimeout(() => {
          if (this.isSubmitting) {
            this.isSubmitting = false;
            this.clearButtonLoading();
          }
        }, 5000);
      });
    }

    clearButtonLoading() {
      const buyBtns = this.el.querySelector('buy-buttons');
      if (!buyBtns) return;

      const btn = buyBtns.querySelector('[data-ups-btn]') || buyBtns.querySelector('button[is="custom-button"]');
      if (!btn) return;

      // Remove aria-busy to restore normal state
      btn.removeAttribute('aria-busy');
    }

    /* ═══════════════════════════════════════════════
       Button State
       ═══════════════════════════════════════════════ */
    setInitialButtonState() {
      if (!this.config.hasSizeOption) return;
      this.updateButtonState();
    }

    initBisTrigger() {
      // Tag the main button with data-ups-btn for reliable selection.
      const buyBtns = this.el.querySelector('buy-buttons');
      if (!buyBtns) return;
      const btn = buyBtns.querySelector('button[is="custom-button"]')
                || buyBtns.querySelector(':scope > button')
                || buyBtns.querySelector('button[type="submit"], button[type="button"]');
      if (!btn) return;
      btn.setAttribute('data-ups-btn', '');
    }

    updateButtonState() {
      const buyBtns = this.el.querySelector('buy-buttons');
      if (!buyBtns) return;

      const form = document.getElementById(this.formId);
      const paymentButton = form ? form.querySelector('.shopify-payment-button') : null;
      const btn = buyBtns.querySelector('[data-ups-btn]')
                || buyBtns.querySelector('button[is="custom-button"]')
                || buyBtns.querySelector('button[type="submit"], button[type="button"], button[disabled]');
      if (!btn) return;

      // Don't update button state if it's currently loading
      if (this.showingAddedFeedback || this.isSubmitting || btn.classList.contains('is-loading')) {
        return;
      }

      if (this.config.requireSizeSelection && this.config.hasSizeOption && !this.sizeExplicitlySelected) {
        // Size selection required & no size selected → dimmed "Pick a size"
        buyBtns.classList.add('ups__atc-dimmed');
        btn.hidden = false;
        btn.style.display = '';
        btn.setAttribute('type', 'submit');
        btn.classList.remove('klaviyo-bis-trigger');
        btn.removeAttribute('data-bis-bound');
        this.setButtonLabel(btn, this.config.pickSizeLabel);
        btn.disabled = false;
        if (paymentButton) paymentButton.style.display = '';
      } else if (this.sizeExplicitlySelected || !this.config.requireSizeSelection) {
        // Size selected OR size selection not required → check variant availability
        const idInput = form ? form.querySelector('input[name="id"]') : null;
        const variantId = idInput ? parseInt(idInput.value, 10) : null;
        const variant = variantId ? (this.config.variants || []).find(v => v.id === variantId) : null;

        if (variant && this.isEffectivelyAvailable(variant)) {
          // Available → normal ATC button
          buyBtns.classList.remove('ups__atc-dimmed');
          btn.hidden = false;
          btn.style.display = '';
          btn.setAttribute('type', 'submit');
          btn.classList.remove('klaviyo-bis-trigger');
          btn.removeAttribute('data-bis-bound');
          this.setButtonLabel(btn, this.config.atcEnabledLabel);
          btn.disabled = false;
          if (paymentButton) paymentButton.style.display = '';
        } else if (variant && !this.isEffectivelyAvailable(variant)) {
          buyBtns.classList.remove('ups__atc-dimmed');
          btn.hidden = false;
          btn.style.display = '';

          // "Notify Me" only for GLOBAL out-of-stock. A CA-location-only OOS variant is still in
          // stock globally, so Klaviyo BIS would fire a false alert — show plain "Sold out" instead.
          if (this.config.hasBackInStock && !variant.available) {
            // OOS + back-in-stock → "Notify Me When Available" with klaviyo-bis-trigger
            btn.setAttribute('type', 'button');
            btn.classList.add('klaviyo-bis-trigger');
            this.setButtonLabel(btn, 'Notify Me When Available');
            btn.disabled = false;
            // Ensure the click handler is bound (idempotent via refreshKlaviyoBis)
            if (typeof window.refreshKlaviyoBis === 'function') window.refreshKlaviyoBis();
          } else {
            // OOS (or CA-location OOS) → disabled "Sold out"
            btn.setAttribute('type', 'button');
            btn.classList.remove('klaviyo-bis-trigger');
            btn.removeAttribute('data-bis-bound');
            this.setButtonLabel(btn, 'Sold out');
            btn.disabled = true;
          }
          if (paymentButton) paymentButton.style.display = 'none';
        }
      }
    }

    /* ═══════════════════════════════════════════════
       Error Handling
       ═══════════════════════════════════════════════ */
    showSizeError() {
      // Show error message
      const errEl = this.el.querySelector('[data-ups-error]');
      if (errEl) {
        errEl.textContent = this.config.sizeRequiredMessage;
        errEl.classList.add('show');
      }

      // Pulse the size fieldset
      const fieldset = this.el.querySelector('[data-size-option]');
      if (fieldset) {
        fieldset.classList.remove('ups-pulse-outline');
        void fieldset.offsetWidth; // Force reflow
        fieldset.classList.add('ups-pulse-outline');

        fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });

        fieldset.addEventListener('animationend', () => {
          fieldset.classList.remove('ups-pulse-outline');
        }, { once: true });
      }

      // Announce to SR
      this.announce(this.config.sizeRequiredMessage);
    }

    hideError() {
      const errEl = this.el.querySelector('[data-ups-error]');
      if (errEl) {
        errEl.classList.remove('show');
        errEl.textContent = '';
      }
    }

    /* ═══════════════════════════════════════════════
       Size-Specific OOS
       ═══════════════════════════════════════════════ */
    updateSizeSpecificOos() {
      if (!this.sizeExplicitlySelected) return;

      const sizeInput = this.el.querySelector('[data-ups-sizes] input[type="radio"]:checked');
      if (!sizeInput) return;

      const selectedSize = sizeInput.getAttribute('title') || sizeInput.value;
      if (!selectedSize) return;

      // For each product in the active grid, check if the selected size is available
      const activePanel = this.el.querySelector('.ups__grid-panel.active');
      if (!activePanel) return;

      const items = activePanel.querySelectorAll('.ups__product');
      items.forEach(item => {
        // Remove any existing size-specific overlay
        const existing = item.querySelector('.ups__oos-overlay-size');
        if (existing) existing.remove();
        item.classList.remove('is-oos-size');

        // We need variant data for each product — use the JSON API cache or data attributes
        // For server-rendered products we can use the collections JSON API
        const handle = item.dataset.productHandle;
        if (!handle || handle === this.currentHandle) return;

        // Fetch from cache or mark for later
        this.checkSizeAvailability(handle, selectedSize, item);
      });
    }

    async checkSizeAvailability(handle, selectedSize, itemEl) {
      const cacheKey = `ups-variants-${handle}`;
      let variants = null;

      // Check sessionStorage cache
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.ts < this.config.cacheTTL * 60 * 1000) {
            variants = parsed.data;
          }
        }
      } catch (_) {}

      if (!variants) {
        try {
          const res = await fetch(`${ROOT}products/${handle}.js`, { credentials: 'same-origin' });
          if (!res.ok) return;
          const data = await res.json();
          variants = data.variants || [];

          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: variants }));
          } catch (_) {}
        } catch (_) {
          return;
        }
      }

      const sizeIdx = this.config.sizeOptionIndex || 0;
      const variant = variants.find(v => {
        const opt = v.options ? v.options[sizeIdx] : (v['option' + (sizeIdx + 1)]);
        return opt === selectedSize;
      });

      if (variant && (!variant.available || (variant.inventory_quantity !== undefined && variant.inventory_quantity <= 0 && variant.inventory_policy !== 'continue'))) {
        itemEl.classList.add('is-oos-size');
        // Inject overlay if not already present
        if (!itemEl.querySelector('.ups__oos-overlay-size')) {
          const wrapper = itemEl.querySelector('.ups__product-img');
          if (wrapper) {
            const overlay = document.createElement('div');
            overlay.className = 'ups__oos-overlay-size';
            overlay.setAttribute('aria-hidden', 'true');
            wrapper.appendChild(overlay);
          }
        }
      }
    }

    /* ═══════════════════════════════════════════════
       State Persistence
       ═══════════════════════════════════════════════ */
    saveState() {
      const state = {
        tab: this.activeTab,
        size: null
      };

      if (this.sizeExplicitlySelected) {
        const sizeInput = this.el.querySelector('[data-ups-sizes] input[type="radio"]:checked');
        if (sizeInput) {
          state.size = sizeInput.getAttribute('title') || sizeInput.value;
        }
      }

      try {
        sessionStorage.setItem('ups-state', JSON.stringify(state));
      } catch (_) {}
    }

    restoreState() {
      let state = null;
      try {
        const raw = sessionStorage.getItem('ups-state');
        if (raw) state = JSON.parse(raw);
      } catch (_) {}

      if (!state) return;

      // Only restore tab if the selected product is in the saved tab's panel
      // (prevents overriding when navigating to a product in a different collection)
      if (state.tab && state.tab !== this.activeTab) {
        const savedPanel = this.el.querySelector(`.ups__grid-panel[data-collection-handle="${state.tab}"]`);
        const hasCurrentProduct = savedPanel && savedPanel.querySelector('.ups__product.is-selected');
        if (hasCurrentProduct) {
          const tabBtn = this.el.querySelector(`.ups__filter-tab[data-collection-handle="${state.tab}"]`);
          if (tabBtn) tabBtn.click();
        }
      }

      // Restore size
      if (state.size && this.config.hasSizeOption) {
        this.restoreSize(state.size);
      }
    }

    restoreSize(savedSize) {
      const radios = this.getSizeRadios();
      const match = radios.find(r => {
        const title = (r.getAttribute('title') || r.value || '').toLowerCase().trim();
        return title === savedSize.toLowerCase().trim();
      });

      if (match && !match.closest('.is-disabled')) {
        // Size available → select it
        const label = this.el.querySelector(`label[for="${CSS.escape(match.id)}"]`);
        if (label) {
          label.click();
        } else {
          match.checked = true;
          match.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.sizeExplicitlySelected = true;
        this.updateButtonState();
      } else {
        // Size unavailable → stay on "Pick a size"
        this.sizeExplicitlySelected = false;
        this.updateButtonState();
      }
    }

    getSizeRadios() {
      return Array.from(this.el.querySelectorAll('[data-ups-sizes] input[type="radio"]'));
    }

    autoSelectFirstSize() {
      const radios = this.getSizeRadios();
      if (!radios.length) return;

      // Find the first available (not disabled) size
      const firstAvailable = radios.find(r => !r.closest('.is-disabled') && !r.disabled);
      if (!firstAvailable) return;

      // Select it - this will trigger the change event handler which does all the work
      const label = this.el.querySelector(`label[for="${CSS.escape(firstAvailable.id)}"]`);
      if (label) {
        label.click();
      } else {
        firstAvailable.checked = true;
        firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ═══════════════════════════════════════════════
       Mobile Scroll
       ═══════════════════════════════════════════════ */
    isMobile() {
      return window.matchMedia('(max-width: 999px)').matches;
    }

    initMobileScroll() {
      this.el.querySelectorAll('.ups__grid').forEach(grid => {
        const panel = grid.closest('.ups__grid-panel');
        const handle = panel ? panel.dataset.collectionHandle : null;
        if (!handle) return;

        grid.addEventListener('scroll', () => {
          this.scrollState.set(handle, grid.scrollLeft);
        }, { passive: true });
      });

      // Initial scroll to selected
      const activePanel = this.el.querySelector('.ups__grid-panel.active');
      if (activePanel) {
        requestAnimationFrame(() => {
          this.scrollToSelected(activePanel.dataset.collectionHandle);
        });
      }
    }

    scrollToSelected(collectionHandle) {
      const saved = this.scrollState.get(collectionHandle);
      const panel = this.el.querySelector(`.ups__grid-panel[data-collection-handle="${collectionHandle}"]`);
      if (!panel) return;

      const grid = panel.querySelector('.ups__grid');
      if (!grid) return;

      if (saved !== undefined) {
        grid.scrollLeft = saved;
        return;
      }

      const selected = grid.querySelector('.ups__product.is-selected');
      if (!selected) return;

      const gridRect = grid.getBoundingClientRect();
      const selRect = selected.getBoundingClientRect();
      const offset = selRect.left - gridRect.left + grid.scrollLeft - 8;
      grid.scrollTo({ left: Math.max(0, offset), behavior: 'auto' });
      this.scrollState.set(collectionHandle, grid.scrollLeft);
    }

    /* ═══════════════════════════════════════════════
       Utility
       ═══════════════════════════════════════════════ */
    announce(msg) {
      const region = this.el.querySelector('[data-ups-announce]');
      if (region) {
        region.textContent = msg;
        setTimeout(() => (region.textContent = ''), 3000);
      }
    }

    showAddedFeedback() {
      const btn = this.getSubmitButton();
      if (!btn) return;

      this.showingAddedFeedback = true;
      this.setButtonLabel(btn, 'Added!');

      if (this.addedFeedbackTimer) {
        clearTimeout(this.addedFeedbackTimer);
      }

      this.addedFeedbackTimer = setTimeout(() => {
        this.showingAddedFeedback = false;
        this.updateButtonState();
      }, 1200);
    }

    getSubmitButton() {
      const buyBtns = this.el.querySelector('buy-buttons');
      if (!buyBtns) return null;
      return buyBtns.querySelector('[data-ups-btn]') || buyBtns.querySelector('button[is="custom-button"]');
    }

    setButtonLabel(btn, label) {
      if (!btn) return;

      const contentEl = btn.contentElement || btn.querySelector(':scope > div');
      if (contentEl) {
        contentEl.textContent = label;
      } else {
        btn.textContent = label;
      }
    }
  }

  /* ═══════════════════════════════════════════════
     Auto-initialize
     ═══════════════════════════════════════════════ */
  function init() {
    document.querySelectorAll('[data-ups]').forEach(el => {
      if (!el.dataset.upsInit) {
        new UnifiedProductSelector(el);
        el.dataset.upsInit = 'true';
      }
    });
  }

  // Handle both initial load and AJAX re-renders
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after section rendering (Shopify theme editor + AJAX product switch)
  document.addEventListener('shopify:section:load', init);

  // Also observe DOM for dynamically inserted UPS components (scoped to main content)
  const observerRoot = document.querySelector('main') || document.body;
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-ups]') && !node.dataset.upsInit) {
          new UnifiedProductSelector(node);
          node.dataset.upsInit = 'true';
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('[data-ups]:not([data-ups-init])').forEach(el => {
            new UnifiedProductSelector(el);
            el.dataset.upsInit = 'true';
          });
        }
      }
    }
  });
  observer.observe(observerRoot, { childList: true, subtree: true });

  // Handle browser back/forward
  window.addEventListener('popstate', () => {
    // Extract the handle regardless of any market/locale prefix (e.g. /en-ca/products/x).
    const handleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    const handle = handleMatch ? handleMatch[1] : '';
    if (!handle) return;

    const currentUps = document.querySelector('[data-ups]');
    if (currentUps && currentUps.dataset.currentHandle !== handle) {
      // Re-fetch the page for the correct product
      window.location.reload();
    }
  });
})();
